import { getMessaging, getToken, isSupported } from 'firebase/messaging'
import { arrayRemove, arrayUnion, doc, serverTimestamp, updateDoc } from 'firebase/firestore'
import { logger } from '@upadelmatch/core'
import { app, db } from './client'

// Web-push registration (release R1). Tokens live on the OWN users doc
// (`fcmTokens: string[]` — owner-writable); the notification CFs read them
// via the admin SDK and prune the ones FCM reports dead. getToken uses the
// Firebase SDK's built-in default VAPID key — no console key setup needed.

export type PushSetupResult = 'granted' | 'denied' | 'default' | 'unsupported' | 'error'

export async function pushSupported(): Promise<boolean> {
  if (typeof Notification === 'undefined' || !('serviceWorker' in navigator)) return false
  return isSupported().catch(() => false)
}

export function pushPermission(): NotificationPermission | 'unsupported' {
  return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission
}

/** Ask for permission (call ONLY from an in-context user gesture — never on
 *  load) and persist the device token + the setting. */
export async function enablePush(uid: string): Promise<PushSetupResult> {
  try {
    if (!(await pushSupported())) return 'unsupported'
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return permission
    const registration = await navigator.serviceWorker.ready
    const token = await getToken(getMessaging(app), { serviceWorkerRegistration: registration })
    if (!token) return 'error'
    await updateDoc(doc(db, 'users', uid), {
      fcmTokens: arrayUnion(token),
      'notificationPreferences.browserPushNotifications': true,
      updatedAt: serverTimestamp(),
    })
    logger.i('push enabled')
    return 'granted'
  } catch (e) {
    logger.w('push setup failed', e)
    return 'error'
  }
}

/** Flip the setting off (CFs respect it); drops this device's token too. */
export async function disablePush(uid: string): Promise<void> {
  try {
    const patch: Record<string, unknown> = {
      'notificationPreferences.browserPushNotifications': false,
      updatedAt: serverTimestamp(),
    }
    if (await pushSupported()) {
      const registration = await navigator.serviceWorker.ready
      const token = await getToken(getMessaging(app), { serviceWorkerRegistration: registration }).catch(
        () => null,
      )
      if (token) patch['fcmTokens'] = arrayRemove(token)
    }
    await updateDoc(doc(db, 'users', uid), patch)
  } catch (e) {
    logger.w('push disable failed', e)
  }
}

/**
 * Silent token refresh — call on every authenticated boot. FCM tokens ROTATE,
 * and permission can be granted while no token ever registered (the gap that
 * hid the first real-device test, 2026-07-05): when permission is already
 * granted this re-registers WITHOUT prompting; otherwise it's a no-op.
 */
export async function refreshPushToken(uid: string): Promise<void> {
  try {
    if (pushPermission() !== 'granted') return
    if (!(await pushSupported())) return
    const registration = await navigator.serviceWorker.ready
    const token = await getToken(getMessaging(app), { serviceWorkerRegistration: registration })
    if (!token) return
    await updateDoc(doc(db, 'users', uid), {
      fcmTokens: arrayUnion(token),
      updatedAt: serverTimestamp(),
    })
  } catch (e) {
    logger.w('push token refresh failed', e)
  }
}
